/**
 * 공중계정 단계 모델 — 단계 라벨·주 버튼·정렬을 한 곳에서 정한다.
 * (SPEC-CHINA-WECHAT-OPEN-PLATFORM-001, 승인된 IA docs/wechat-open/ui-ia.md)
 *
 * 서버가 stage/nextAction 을 주면 그대로 쓰고, 서버가 아직 안 주는 환경에서는
 * accountRows.js 어댑터가 deriveStage() 로 같은 규칙을 계산한다(서버 규칙의 사본 — 서버 확정 시 제거).
 */

export const STAGE = {
    not_connected: { labelKey: 'wxopen_stage_not_connected', color: 'default', rank: 5 },
    invited: { labelKey: 'wxopen_stage_invited', color: 'processing', rank: 4 },
    authorized_partial: { labelKey: 'wxopen_stage_authorized_partial', color: 'warning', rank: 2 },
    authorized_ready: { labelKey: 'wxopen_stage_authorized_ready', color: 'success', rank: 3 },
    component_active: { labelKey: 'wxopen_stage_component_active', color: 'blue', rank: 6 },
    unauthorized: { labelKey: 'wxopen_stage_unauthorized', color: 'error', rank: 1 },
    token_error: { labelKey: 'wxopen_stage_token_error', color: 'error', rank: 0 },
};

export const ACTION = {
    create_link: { labelKey: 'wxopen_act_create_link', type: 'primary', makesLink: true },
    new_link: { labelKey: 'wxopen_act_new_link', type: 'primary', makesLink: true },
    add_perm_link: { labelKey: 'wxopen_act_add_perm_link', type: 'primary', makesLink: true },
    to_component: { labelKey: 'wxopen_act_to_component', type: 'primary', makesLink: false },
    to_self: { labelKey: 'wxopen_act_to_self', type: 'default', makesLink: false },
};

const NEXT_TO_ACTION = {
    create_invite: 'create_link',
    resend_invite: 'new_link',
    request_reauthorize: 'add_perm_link',
    switch_to_component: 'to_component',
    switch_to_self: 'to_self',
};

/** 초대 status 가 계약에 없어 "끝난 값이 아니면 유효"로 판정한다(계약 확정 시 좁힌다). */
export function isInviteActive(status) {
    return !['used', 'revoked', 'expired'].includes(String(status || '').toLowerCase());
}

/** 서버 account-stage.ts 와 같은 규칙(서버가 stage 를 주지 않는 환경 전용). */
export function deriveStage({ registered = true, authMode = 'self', authorizerStatus, missingCount = 0, hasActiveInvitation = false }) {
    if (!registered) return { stage: 'unregistered_authorizer', nextAction: 'none' };
    if (!authorizerStatus) {
        return hasActiveInvitation
            ? { stage: 'invited', nextAction: 'resend_invite' }
            : { stage: 'not_connected', nextAction: 'create_invite' };
    }
    if (authorizerStatus === 'unauthorized') {
        return authMode === 'component'
            ? { stage: 'unauthorized', nextAction: 'switch_to_self' }
            : { stage: 'unauthorized', nextAction: hasActiveInvitation ? 'resend_invite' : 'create_invite' };
    }
    if (authorizerStatus === 'refresh_failed') {
        return authMode === 'component'
            ? { stage: 'token_error', nextAction: 'switch_to_self' }
            : { stage: 'token_error', nextAction: 'request_reauthorize' };
    }
    if (missingCount > 0) {
        return authMode === 'component'
            ? { stage: 'authorized_partial', nextAction: 'switch_to_self' }
            : { stage: 'authorized_partial', nextAction: 'request_reauthorize' };
    }
    return authMode === 'component'
        ? { stage: 'component_active', nextAction: 'none' }
        : { stage: 'authorized_ready', nextAction: 'switch_to_component' };
}

/** 서버/어댑터 행 -> 화면 행. 모르는 필드는 버린다(화면에 DB id 가 새지 않게 허용 필드만 복사). */
export function normalizeAccountRow(raw, index = 0) {
    const r = raw || {};
    const missingFuncs = Array.isArray(r.missingFuncs) ? r.missingFuncs : [];
    const grantedFuncs = Array.isArray(r.grantedFuncs) ? r.grantedFuncs : [];
    const authMode = r.authMode === 'component' ? 'component' : 'self';
    // 유효한 초대: 서버는 가장 최근 초대(latestInvitation)만 주며 status 가 active 일 때만 「승인 대기」다.
    const li = r.latestInvitation;
    const activeInvitation = li && li.status === 'active' && li.expiresAt ? { expiresAt: li.expiresAt } : null;
    let stage = r.stage;
    let nextAction = r.nextAction;
    // gzhName 이 없는 행 = 등록되지 않은 authorizer
    const unregistered = stage === 'unregistered_authorizer' || !r.gzhName;
    if (unregistered || !STAGE[stage]) {
        // 서버가 등록 안 된 행의 stage 를 unregistered_authorizer 로 주므로, 라벨은 실제 상태(토큰·권한)로 다시 계산한다.
        const d = deriveStage({
            registered: true,
            authMode,
            authorizerStatus: r.tokenState === 'error' ? 'refresh_failed' : 'authorized',
            missingCount: missingFuncs.length,
            hasActiveInvitation: !!activeInvitation,
        });
        stage = d.stage;
        nextAction = unregistered ? 'none' : d.nextAction;
    }
    return {
        rowKey: r.gzhName || `unregistered-${index}`,
        gzhName: r.gzhName || '',
        displayName: r.displayName || r.gzhDisplayName || r.nickName || r.gzhName || '',
        accountType: r.serviceType || r.accountType || '',
        tenantName: r.tenantName || '',
        unregistered,
        stage,
        nextAction: nextAction || 'none',
        authMode,
        tokenState: r.tokenState || '',
        missingFuncs,
        grantedFuncs,
        authorizedAt: r.authorizedAt || null,
        activeInvitation,
    };
}

/** 행의 주 버튼 하나. 버튼이 없으면 null. */
export function primaryAction(row) {
    if (!row || row.unregistered) return null;
    const mapped = NEXT_TO_ACTION[row.nextAction];
    if (mapped) return mapped;
    if (row.stage === 'component_active') return 'to_self';
    return null;
}

/** 링크를 만들 때 서버에 보내는 용도(화면에는 안 나온다). 이미 위임 기록이 있으면 권한 추가용. */
export function linkPurpose(row) {
    return ['authorized_partial', 'authorized_ready', 'component_active', 'token_error'].includes(row && row.stage)
        ? 'reauthorize'
        : 'authorize';
}

/** 조치가 필요한 단계가 위로. 같은 단계는 이름순. */
export function sortRows(rows) {
    return [...(rows || [])].sort((a, b) => {
        const ra = STAGE[a.stage] ? STAGE[a.stage].rank : 9;
        const rb = STAGE[b.stage] ? STAGE[b.stage].rank : 9;
        if (ra !== rb) return ra - rb;
        return String(a.displayName).localeCompare(String(b.displayName), 'ko');
    });
}

export function summarize(rows) {
    const list = rows || [];
    return { active: list.filter((r) => r.stage === 'component_active').length, total: list.length };
}

/** 초대 이력 상태 -> i18n 키(같은 단어를 목록의 「승인 대기」 계열 문구와 맞춘다). */
export function inviteStatusKey(status) {
    const s = String(status || '').toLowerCase();
    if (s === 'used') return 'wxopen_inv_used';
    if (s === 'expired') return 'wxopen_inv_expired';
    if (s === 'revoked') return 'wxopen_inv_revoked';
    return 'wxopen_inv_pending';
}
